/**
 * SessionValidationDialog - shows connection validation progress when resuming a session
 *
 * This dialog handles the full validation flow:
 * 1. Validating phase: Shows progress while testing connections
 * 2. Complete phase: All connections succeeded, auto-closes
 * 3. Has-failures phase: Shows per-connection decision prompts for failures
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { AlertTriangle, CheckCircle2, Clock, KeyRound } from 'lucide-react';
import {
  ConnectionValidationStatus,
  type ConnectionValidationState,
} from './ConnectionValidationStatus';
import {
  FailedConnectionPrompt,
  type ConnectionDecision,
} from './FailedConnectionPrompt';
import { NewConnectionPopup } from '../../connections/components/NewConnectionPopup';
import {
  validateSessionConnections,
  allConnectionsSucceeded,
  getFailedConnections,
  allDecisionsMade,
  createPendingDecisions,
  type ValidationResult,
  type ValidationProgress,
} from '../services/connectionValidationService';
import {
  flushPendingSessionAutoSaveBeforeRestore,
  pauseSessionAutoSave,
  resumeSessionAutoSave,
} from '../services/sessionAutoSave';
import type { Session, FailedConnectionDecision } from '../types/sessionTypes';
import type { Connection, SaveConnectionOptions } from '../../../types/electron';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { redactSecretText, safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

// ============================================================================
// Types
// ============================================================================

type ValidationPhase = 'validating' | 'complete' | 'has-failures';

interface ConnectionState {
  connectionId: string;
  connectionName: string;
  serverInfo?: string;
  state: ConnectionValidationState;
  error?: string;
  /** Only set alongside `state: 'deferred'` — see ConnectionValidationStatus. */
  deferredReason?: 'background' | 'password';
}

interface SessionValidationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** The session being validated */
  session: Session;
  /** All saved connections */
  savedConnections: Connection[];
  /** Called when validation succeeds or user makes decisions on failures */
  onValidationComplete: (results: ValidationResult[], decisions: FailedConnectionDecision[]) => void;
  /** Called when user cancels validation */
  onCancel: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function SessionValidationDialog({
  open,
  session,
  savedConnections,
  onValidationComplete,
  onCancel,
}: SessionValidationDialogProps) {
  const [phase, setPhase] = useState<ValidationPhase>('validating');
  const [progress, setProgress] = useState<ValidationProgress | null>(null);
  const [connectionStates, setConnectionStates] = useState<ConnectionState[]>([]);
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [decisions, setDecisions] = useState<FailedConnectionDecision[]>([]);
  const [retryingConnectionIds, setRetryingConnectionIds] = useState<Set<string>>(new Set());
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const initializedSessionIdRef = useRef<string | null>(null);
  const isDialogActiveRef = useRef(false);
  /** Aborts the validation pass when the dialog closes (Cancel, X, Esc). */
  const validationAbortRef = useRef<AbortController | null>(null);
  /**
   * Set by "Continue in background", and the difference between the two ways
   * this dialog can close.
   *
   * Cancel/X/Esc mean *stop* — abort the pass and withdraw whatever is dialing.
   * "Continue in background" means *detach*: the user still wants every one of
   * these connections, they just don't want to watch. Aborting there would
   * cancel dials the user explicitly asked to continue — and a withdrawal that
   * lands while a dial is in flight makes main close the pool at settle and
   * report `Connection cancelled`, which is the failure mode that made a
   * perfectly healthy connection come back Disconnected.
   */
  const detachedRef = useRef(false);
  /** Connections a Retry click has dialing right now — abandoned on close. */
  const retryDialingIdsRef = useRef<Set<string>>(new Set());
  /**
   * Re-entry guards for one open. Refs, not state: a zustand write re-renders
   * through `useSyncExternalStore` in the SYNC lane, and that render still sees
   * the DEFAULT-lane `isValidating` / `phase` updates as pending — so a state
   * guard reads "not started" for a pass that is running, or has finished. That
   * is how one resume became a restore loop that froze the renderer (1.5.0):
   * the re-rendered effect started a pass, the already-open connection let it
   * complete in microtasks, the restore wrote stores, and round again, with the
   * default-lane render that would have closed the dialog never getting a turn.
   *
   * Both reset when the dialog initializes for an open (a reopen, or another
   * session). "Pass started" also resets with the open effect's cleanup —
   * close, unmount, and StrictMode's remount, whose aborted pass never reports;
   * "completion reported" does not, so StrictMode's second run cannot report a
   * second time.
   */
  const passStartedRef = useRef(false);
  const completionReportedRef = useRef(false);
  /** Latest callback, so a new closure per parent render never re-runs the pass. */
  const onValidationCompleteRef = useRef(onValidationComplete);
  onValidationCompleteRef.current = onValidationComplete;

  /** The one way out towards a restore — at most once per open. */
  const reportCompletion = useCallback(
    (completedResults: ValidationResult[], completedDecisions: FailedConnectionDecision[]) => {
      if (completionReportedRef.current) return;
      completionReportedRef.current = true;
      onValidationCompleteRef.current(completedResults, completedDecisions);
    },
    []
  );

  useEffect(() => {
    isDialogActiveRef.current = open;
    // Captured for the cleanup below: the Set's identity never changes (only
    // its contents), so this is the same live set the retry handler mutates.
    const retryDialingIds = retryDialingIdsRef.current;

    // Pause auto-save when dialog opens to prevent session corruption during validation
    if (open) {
      // Same flush-before-restore rule `loadSession` follows, applied one step
      // earlier: pausing DROPS the queued save, so without this a tab the user
      // opened in the last 500ms would vanish on the resume-with-validation
      // path while surviving the resume-without-validation one. A no-op inside
      // a session switch — that window is already paused, so nothing is queued.
      flushPendingSessionAutoSaveBeforeRestore();
      pauseSessionAutoSave();
    }

    return () => {
      isDialogActiveRef.current = false;
      passStartedRef.current = false;
      // Stopping the UI from listening is not the same as stopping the work.
      // Abort the pass so it stops walking the remaining connections, and
      // withdraw the intent behind anything already dialing (validation's
      // current one via the signal, plus any Retry the user fired) so it is
      // closed on arrival instead of adopted.
      if (!detachedRef.current) {
        validationAbortRef.current?.abort();
      }
      validationAbortRef.current = null;
      if (retryDialingIds.size > 0 && !detachedRef.current) {
        const { abandonConnect } = useConnectionStore.getState();
        retryDialingIds.forEach((id) => abandonConnect(id));
        retryDialingIds.clear();
      }
      // Resume auto-save when dialog closes — but only against our own pause.
      // Pauses nest (a session switch holds one across this whole dialog), and
      // an unmatched resume here would hand auto-save back mid-teardown.
      if (open) {
        resumeSessionAutoSave();
      }
    };
  }, [open]);

  // Initialize connection states from session
  useEffect(() => {
    if (!open) {
      initializedSessionIdRef.current = null;
      setProgress(null);
      setIsValidating(false);
      return;
    }

    if (initializedSessionIdRef.current === session.id) {
      return;
    }
    initializedSessionIdRef.current = session.id;
    passStartedRef.current = false;
    completionReportedRef.current = false;

    const states: ConnectionState[] = session.connections.map(conn => {
      const savedConn = savedConnections.find(c => c.id === conn.connectionId);
      return {
        connectionId: conn.connectionId,
        connectionName: safeConnectionName(savedConn, 'Unknown'),
        serverInfo: savedConn ? safeConnectionServer(savedConn) : undefined,
        state: 'waiting' as ConnectionValidationState,
      };
    });
    setConnectionStates(states);
    setPhase('validating');
    setProgress(null);
    setResults([]);
    setDecisions([]);
    setRetryingConnectionIds(new Set());
  }, [open, session, savedConnections]);

  // Handle progress updates
  const handleProgress = useCallback((prog: ValidationProgress) => {
    setProgress(prog);

    // Update connection states based on progress
    setConnectionStates(prev => {
      const resultMap = new Map(prog.results.map(r => [r.connectionId, r]));

      return prev.map(conn => {
        const result = resultMap.get(conn.connectionId);
        if (result) {
          return {
            ...conn,
            // A deferred connection is neither connected nor failed — say so,
            // rather than showing a green check for something that isn't open.
            state: result.deferred ? 'deferred' : result.success ? 'connected' : 'failed',
            error: result.error,
            deferredReason: result.deferredReason,
          };
        }

        if (prog.current?.connectionId === conn.connectionId) {
          return { ...conn, state: 'connecting' };
        }

        return conn;
      });
    });
  }, []);

  // Run validation when dialog opens
  useEffect(() => {
    if (!open || isValidating || phase !== 'validating') return;
    // The state above can be stale (see `passStartedRef`); this cannot. Set
    // before anything that can call back or write a store.
    if (passStartedRef.current) return;
    passStartedRef.current = true;
    if (session.connections.length === 0) {
      // No connections to validate
      setPhase('complete');
      reportCompletion([], []);
      return;
    }

    setIsValidating(true);

    const abortController = new AbortController();
    validationAbortRef.current = abortController;

    /**
     * Is this pass still the one the dialog is showing?
     *
     * An ABORTED or SUPERSEDED pass must report nothing at all. It used to,
     * and that cost a healthy connection: a pass aborted before it dialed
     * anything resolves with `[]`, and `allConnectionsSucceeded([])` is `true`
     * (`[].every` is vacuous) — so the dead pass announced "everything
     * connected", the app closed the dialog on the strength of it, and the
     * unmount aborted the pass that was ACTUALLY dialing. The connection it was
     * on got withdrawn mid-dial, which makes main close the pool at settle: an
     * amber "Disconnected" over a server that had just answered.
     *
     * React 18's StrictMode makes that sequence the NORMAL one in dev — mount,
     * cleanup, mount — so the first pass is always aborted and always resolves
     * empty. The guard is what keeps the second pass the only one talking.
     */
    const isCurrentPass = () =>
      isDialogActiveRef.current &&
      !abortController.signal.aborted &&
      validationAbortRef.current === abortController;

    const handleProgressIfActive = (prog: ValidationProgress) => {
      if (!isCurrentPass()) return;
      handleProgress(prog);
    };

    validateSessionConnections(session, savedConnections, handleProgressIfActive, abortController.signal)
      .then(validationResults => {
        if (!isCurrentPass()) return;
        setResults(validationResults);

        if (allConnectionsSucceeded(validationResults)) {
          setPhase('complete');
          reportCompletion(validationResults, []);
        } else {
          // Create pending decisions for failed connections
          const pendingDecisions = createPendingDecisions(validationResults, savedConnections);
          setDecisions(pendingDecisions);
          setPhase('has-failures');
        }
      })
      .catch(error => {
        if (!isCurrentPass()) return;
        console.error('[SessionValidationDialog] Validation error:', error);
        setPhase('has-failures');
      })
      .finally(() => {
        if (!isCurrentPass()) return;
        setIsValidating(false);
      });
  }, [open, session, savedConnections, handleProgress, isValidating, phase, reportCompletion]);

  // Calculate progress percentage
  const progressPercent = progress
    ? Math.round((progress.completed / Math.max(progress.total, 1)) * 100)
    : 0;

  const failedConnections = getFailedConnections(results);
  // "Succeeded" means a connection was opened. A deferred one succeeded at
  // validation without being opened, so counting it here would tell the user
  // something connected that hasn't.
  const successfulConnections = results.filter(r => r.success && !r.deferred);
  // Two different promises, so two different lines: the background ones are
  // already being opened by the time this is read, the password ones will sit
  // closed until a tab asks for them.
  const backgroundConnections = results.filter(
    r => r.deferred && r.deferredReason !== 'password'
  );
  const passwordConnections = results.filter(r => r.deferred && r.deferredReason === 'password');
  const canContinue = allDecisionsMade(decisions);

  // Handle decision update for a connection
  const updateDecision = (connectionId: string, decision: ConnectionDecision) => {
    setDecisions(prev =>
      prev.map(d =>
        d.connectionId === connectionId ? { ...d, decision } : d
      )
    );
  };

  // Handle retry for a failed connection
  const handleRetry = async (connectionId: string) => {
    const connection = savedConnections.find(c => c.id === connectionId);
    if (!connection) return;

    // Mark as retrying
    setRetryingConnectionIds(prev => new Set([...prev, connectionId]));
    retryDialingIdsRef.current.add(connectionId);

    try {
      const connectionStore = useConnectionStore.getState();
      // Retry inside the session-restore flow — never a first-connect bridge trigger.
      const success = await connectionStore.connectToDatabase(connection, { initiator: 'restore' });
      retryDialingIdsRef.current.delete(connectionId);

      if (success) {
        // Remove from failed connections - update results and decisions
        setResults(prev => prev.map(r =>
          r.connectionId === connectionId ? { ...r, success: true, error: undefined } : r
        ));
        setDecisions(prev => prev.filter(d => d.connectionId !== connectionId));
        // Update connection state to connected
        setConnectionStates(prev => prev.map(c =>
          c.connectionId === connectionId ? { ...c, state: 'connected', error: undefined } : c
        ));

        // Check if all connections are now successful
        const updatedResults = results.map(r =>
          r.connectionId === connectionId ? { ...r, success: true } : r
        );
        if (allConnectionsSucceeded(updatedResults)) {
          setPhase('complete');
          reportCompletion(updatedResults, []);
        }
      } else {
        // Update error message with new error
        const storeError = useConnectionStore.getState().connectionErrorById[connectionId];
        const newError = storeError || 'Failed to connect';
        setResults(prev => prev.map(r =>
          r.connectionId === connectionId ? { ...r, error: newError } : r
        ));
        setDecisions(prev => prev.map(d =>
          d.connectionId === connectionId ? { ...d, error: newError } : d
        ));
        setConnectionStates(prev => prev.map(c =>
          c.connectionId === connectionId ? { ...c, error: newError } : c
        ));
        // Clean up failed connection state
        try {
          await connectionStore.disconnectFromDatabase(connectionId);
        } catch {
          // Ignore cleanup errors
        }
      }
    } catch (error) {
      // Update error message
      const newError = error instanceof Error ? error.message : 'Connection failed';
      setDecisions(prev => prev.map(d =>
        d.connectionId === connectionId ? { ...d, error: newError } : d
      ));
      setConnectionStates(prev => prev.map(c =>
        c.connectionId === connectionId ? { ...c, error: newError } : c
      ));
    } finally {
      retryDialingIdsRef.current.delete(connectionId);
      // Remove from retrying set
      setRetryingConnectionIds(prev => {
        const next = new Set(prev);
        next.delete(connectionId);
        return next;
      });
    }
  };

  // Handle edit for a failed connection
  const handleEdit = (connectionId: string) => {
    const connection = savedConnections.find(c => c.id === connectionId);
    if (connection) {
      setEditingConnection(connection);
    }
  };

  // Handle save after editing a connection
  const handleSaveEdit = async (updatedConnection: Connection, options: SaveConnectionOptions): Promise<boolean> => {
    const connectionStore = useConnectionStore.getState();
    await connectionStore.updateConnection(updatedConnection, options);
    setEditingConnection(null);
    // Automatically retry connection after editing
    handleRetry(updatedConnection.id);
    return true;
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingConnection(null);
  };

  // Handle continue with decisions
  const handleContinue = () => {
    reportCompletion(results, decisions);
  };

  /**
   * Hand the workspace back now and let the remaining connections finish on
   * their own. Available from the moment the active tab's connections have
   * settled — before that, hiding would produce a workspace whose front tab
   * still can't run anything.
   *
   * Detaches rather than cancels (see `detachedRef`), and passes the results
   * collected so far: the connections still dialing simply have no result yet,
   * which is the same shape the restore already handles for a connection it
   * deferred. They keep dialing, and the Object Explorer says "Connecting…"
   * for each until it lands.
   */
  const tabsReady = progress?.tabsReady === true;
  const handleContinueInBackground = () => {
    detachedRef.current = true;
    void window.electronAPI?.analytics
      ?.track('feature:session_resume_continue_background', {
        connectionsStillOpening: Math.max(
          (progress?.total ?? 0) - (progress?.completed ?? 0),
          0
        ),
      })
      .catch(() => {});
    reportCompletion(progress?.results ?? [], []);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent
        className="sm:max-w-[500px]"
        data-testid="session-validation-dialog"
        // Prevent closing during validation
        onEscapeKeyDown={(e) => {
          if (phase === 'validating') e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (phase === 'validating') e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {phase === 'validating' && 'Resuming Session'}
            {phase === 'complete' && 'Session Loaded'}
            {phase === 'has-failures' && 'Connection Issues'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Status message */}
          {phase === 'validating' && (
            <p className="text-sm text-muted-foreground">
              Validating connections...
            </p>
          )}

          {phase === 'has-failures' && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {failedConnections.length === 1
                    ? '1 connection could not be established.'
                    : `${failedConnections.length} connections could not be established.`}
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  Choose what to do with each failed connection below.
                </p>
              </div>
            </div>
          )}

          {/* Successful connections summary (only in has-failures phase) */}
          {phase === 'has-failures' && successfulConnections.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              <span>
                {successfulConnections.length === 1
                  ? '1 connection succeeded'
                  : `${successfulConnections.length} connections succeeded`}
              </span>
            </div>
          )}

          {/* Deferred connections — nothing is wrong with them. The restore
              opens them in the background the moment it commits … */}
          {phase === 'has-failures' && backgroundConnections.length > 0 && (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="validation-deferred-summary"
            >
              <Clock className="h-4 w-4" />
              <span>
                {backgroundConnections.length === 1
                  ? '1 connection opens in the background'
                  : `${backgroundConnections.length} connections open in the background`}
              </span>
            </div>
          )}

          {/* … except the ones that have to ask for a password, which really do
              wait for the first tab that needs them. */}
          {phase === 'has-failures' && passwordConnections.length > 0 && (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="validation-password-summary"
            >
              <KeyRound className="h-4 w-4" />
              <span>
                {passwordConnections.length === 1
                  ? '1 connection connects on first use — it needs a password'
                  : `${passwordConnections.length} connections connect on first use — they need a password`}
              </span>
            </div>
          )}

          {/* Connection list during validation */}
          {phase === 'validating' && (
            <div className="border border-border rounded-md divide-y divide-border max-h-[240px] overflow-y-auto">
              {connectionStates.map((conn) => (
                <ConnectionValidationStatus
                  key={conn.connectionId}
                  connectionName={conn.connectionName}
                  serverInfo={conn.serverInfo}
                  state={conn.state}
                  error={conn.error && redactSecretText(conn.error)}
                  deferredReason={conn.deferredReason}
                />
              ))}
            </div>
          )}

          {/* Failed connection prompts */}
          {phase === 'has-failures' && (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {decisions.map((decisionInfo) => {
                const connState = connectionStates.find(
                  c => c.connectionId === decisionInfo.connectionId
                );
                return (
                  <FailedConnectionPrompt
                    key={decisionInfo.connectionId}
                    connectionName={decisionInfo.connectionName}
                    serverInfo={connState?.serverInfo}
                    error={decisionInfo.error && redactSecretText(decisionInfo.error)}
                    decision={decisionInfo.decision}
                    isRetrying={retryingConnectionIds.has(decisionInfo.connectionId)}
                    onRetry={() => handleRetry(decisionInfo.connectionId)}
                    onEdit={() => handleEdit(decisionInfo.connectionId)}
                    onKeep={() => updateDecision(decisionInfo.connectionId, 'keep')}
                    onRemove={() => updateDecision(decisionInfo.connectionId, 'remove')}
                  />
                );
              })}
            </div>
          )}

          {/* Progress bar (only during validation) */}
          {phase === 'validating' && progress && (
            <div className="space-y-2">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-xs text-muted-foreground text-right">
                {progress.completed}/{progress.total}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === 'validating' && (
            <>
              <Button
                variant="outline"
                onClick={onCancel}
                data-testid="cancel-validation-button"
              >
                Cancel
              </Button>
              {/* A disabled button has to say why (span, because a disabled
                  button swallows its own mouse events and the tooltip with
                  them). */}
              <span
                title={
                  tabsReady
                    ? 'Open your workspace now — the remaining connections keep opening, and the sidebar shows their progress.'
                    : 'Available once the connections your open tab needs have finished — until then your workspace would come back unusable.'
                }
              >
                <Button
                  onClick={handleContinueInBackground}
                  disabled={!tabsReady}
                  data-testid="continue-in-background-button"
                >
                  Continue in background
                </Button>
              </span>
            </>
          )}

          {phase === 'has-failures' && (
            <>
              <Button
                variant="outline"
                onClick={onCancel}
                data-testid="back-to-sessions-button"
              >
                Back to Sessions
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!canContinue}
                data-testid="continue-with-decisions-button"
              >
                Continue
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit connection dialog */}
    {editingConnection && (
      <NewConnectionPopup
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        editConnection={editingConnection}
      />
    )}
    </>
  );
}
